/**
 * Screen shake, line-clear flash and lock effects. `docs/prompts/02-client.md`:
 * "Screen shake, line-clear flash and lock effects live here, never in the
 * engine" — the engine only reports *that* a lock happened and *which* rows
 * cleared (`LockResult`); everything about how that looks is this module and
 * `Canvas2DRenderer`.
 *
 * Rows are tracked by the index they had at the instant they cleared, not
 * re-read from the board on later frames — the engine has already compacted
 * the board by the time this fires, so that index no longer names the same
 * cells. That is fine: the renderer only ever uses it as a fixed pixel
 * offset for the flash overlay, the same simplification the reference
 * prototype makes (`reference/mode-demos.html`'s `flashRows`/`flashT`).
 * `kind` is the piece that completed the clear (`LockResult.kind`), used for
 * every cell in the flash rather than each cell's own original piece —
 * capturing the true per-cell history would mean diffing the whole board on
 * every tick for a decorative flash, which is not worth paying for.
 */
import type { Vec2 } from '@interlock/engine';
import type { PaintKind } from '@interlock/themes';

import { ParticlePool } from './particles.js';
import type { ParticleSnapshot } from './particles.js';
import type { ClearEffectProfile } from './theme-effects.js';

const CLEAR_DURATION_MS = 260;
const SHAKE_DURATION_MS = 220;

/** How many particles a single cleared cell spawns at baseline (single line, no combo). Scaled up for a quad and for a running combo — see `triggerClear`. */
const BASE_PARTICLES_PER_CELL = 2;
/** Total pool capacity — the hard cap "capped on low-end" means in practice. A quad with a running combo asks for more than this; the pool recycles its oldest particles rather than growing. */
const PARTICLE_POOL_CAPACITY = 240;
/** Extra accent-coloured glints layered over the ordinary burst when a clear extends a back-to-back streak. */
const B2B_GLINT_COUNT = 6;
const QUAD_RING_DURATION_MS = 420;
const B2B_RING_DURATION_MS = 300;

/**
 * How long a reconciliation cue stays on screen.
 *
 * Long enough to be seen at a glance and short enough not to be read as part of
 * the board. `docs/prompts/06-coop.md`: "Reconciliation never teleports a piece
 * more than one cell without a visible correction cue."
 */
const CORRECTION_DURATION_MS = 320;

export interface ClearingRow {
  readonly kind: PaintKind;
  /** 0 at the instant the row locked in, 1 when the animation is done. */
  readonly progress: number;
}

/** Cells the server moved this player's piece to, and how far through the flash it is. */
export interface CorrectionCue {
  readonly cells: readonly Vec2[];
  /** 0 at the instant of the correction, 1 when the flash is done. */
  readonly progress: number;
}

/** An expanding ring — the quad shockwave, or the smaller back-to-back accent pulse. Centre and radius are in board cell units, same convention as `ClearingRow`/particles. */
export interface RingSnapshot {
  readonly x: number;
  readonly y: number;
  readonly radiusCells: number;
  readonly alpha: number;
  readonly color: string;
}

export interface EffectsSnapshot {
  readonly shakeX: number;
  readonly shakeY: number;
  readonly clearingRows: ReadonlyMap<number, ClearingRow>;
  /** `null` when no reconciliation has moved anything recently. */
  readonly correction: CorrectionCue | null;
  /**
   * Every live clear-burst particle, in board cell units. Optional (rather
   * than always-required) so existing hand-built `EffectsSnapshot` fixtures
   * elsewhere in the test suite keep compiling unchanged — a caller that
   * omits it gets no particles, exactly as if none were ever spawned.
   */
  readonly particles?: readonly ParticleSnapshot[];
  /** The quad shockwave ring — `null` when no quad is currently celebrating. */
  readonly quadRing?: RingSnapshot | null;
  /** The smaller back-to-back accent ring — `null` when nothing is currently extending a streak. */
  readonly accentRing?: RingSnapshot | null;
}

interface RingState {
  elapsedMs: number;
  durationMs: number;
  x: number;
  y: number;
  maxRadiusCells: number;
  color: string;
}

function ringSnapshot(ring: RingState | null): RingSnapshot | null {
  if (ring === null) return null;
  const t = Math.min(1, ring.elapsedMs / ring.durationMs);
  return {
    x: ring.x,
    y: ring.y,
    radiusCells: ring.maxRadiusCells * t,
    alpha: Math.max(0, 1 - t),
    color: ring.color,
  };
}

/** Options accompanying a clear — the escalations beyond the plain flash/shake. */
export interface ClearTriggerOptions {
  /** True for a four-line clear — the biggest celebration: a bigger burst, plus the shockwave ring. */
  readonly quad?: boolean;
  /** The engine's own running combo counter (`LockResult.combo`) — scales the burst; 0 or unset means no combo bonus. */
  readonly comboCount?: number;
  /** True when this clear extends a back-to-back streak — spawns the accent-ring pulse. */
  readonly backToBack?: boolean;
}

/**
 * Whether the player has asked the system for less motion.
 *
 * Read every time rather than once, so flipping the OS setting mid-run takes
 * effect on the next clear. Guarded for an environment with no `matchMedia`
 * (a unit test, a very old embed) — which simply means "no preference".
 */
export function prefersReducedMotion(): boolean {
  try {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export class EffectsController {
  private shakeRemainingMs = 0;
  private shakeMagnitudePx = 0;
  private readonly clearing = new Map<number, { kind: PaintKind; elapsedMs: number }>();
  private correctionCells: readonly Vec2[] = [];
  private correctionElapsedMs = CORRECTION_DURATION_MS;
  private readonly particles = new ParticlePool(PARTICLE_POOL_CAPACITY);
  private quadRing: RingState | null = null;
  private accentRing: RingState | null = null;

  /**
   * @param maxShakePx Screen shake cap. `docs/DESIGN.md` states this
   *   explicitly for Voltage ("capped at 4px"); every theme uses the same
   *   cap here rather than the renderer special-casing one theme's number —
   *   see the phase 02 ADR.
   */
  /**
   * @param reducedMotion `prefers-reduced-motion`, asked on every trigger.
   *   `app.css` already collapses every CSS animation under that
   *   preference, but a canvas is not CSS: the shake and the particle burst
   *   are drawn by hand, so they have to honour it by hand. With it set, a
   *   clear keeps its row flash (the one cue that says *which* rows went,
   *   and it does not travel) and loses the shake, the particles and the
   *   expanding rings.
   */
  constructor(
    private readonly maxShakePx = 4,
    private readonly rng: () => number = Math.random,
    private readonly reducedMotion: () => boolean = prefersReducedMotion,
  ) {}

  /**
   * Start (or restart) the flash for `rows`, painted with `kind`, and spawn
   * the clear-burst particles — cell-coloured sparks (or the theme's own
   * variant of that: embers, motes, shards…) along every cleared row,
   * escalating for a quad and for a running combo/back-to-back streak.
   *
   * `cols` and `profile` come from the caller (`GameScreen.svelte`/
   * `DuelScreen.svelte`), which is the only place that already has both the
   * board width and the active theme — this stays a plain function of
   * numbers otherwise, same as every other method here (`docs/decisions/
   * 0006`: effects are timer-driven state, never draw calls, and now never a
   * `Theme` reference either — `profile` is already-resolved numbers and a
   * colour function, not the theme itself).
   */
  triggerClear(
    rows: readonly number[],
    kind: PaintKind,
    cols: number,
    profile: ClearEffectProfile,
    opts: ClearTriggerOptions = {},
  ): void {
    for (const row of rows) this.clearing.set(row, { kind, elapsedMs: 0 });
    if (rows.length === 0 || cols <= 0) return;
    if (this.reducedMotion()) return;

    const quad = opts.quad === true;
    const backToBack = opts.backToBack === true;
    const comboCount = Math.max(0, opts.comboCount ?? 0);

    // Escalation: a running combo scales the burst up to 1.6x; a quad scales
    // it a further 1.5x on top of that. Both are capped low enough that even
    // a quad-with-combo burst (~4 rows × 10 cols × ~5 particles ≈ 200) stays
    // inside `PARTICLE_POOL_CAPACITY` rather than immediately recycling its
    // own particles mid-spawn.
    const comboMultiplier = 1 + Math.min(6, comboCount) * 0.1;
    const quadMultiplier = quad ? 1.5 : 1;
    const countPerCell = Math.max(
      1,
      Math.round(BASE_PARTICLES_PER_CELL * comboMultiplier * quadMultiplier),
    );
    const color = profile.colorFor(kind);

    for (const row of rows) {
      for (let col = 0; col < cols; col++) {
        for (let i = 0; i < countPerCell; i++) {
          const angle = this.rng() * Math.PI * 2;
          const speed = profile.speed * (0.5 + this.rng() * 0.9);
          this.particles.spawn({
            x: col + 0.5,
            y: row + 0.5,
            vx: Math.cos(angle) * speed,
            // A slight upward bias so the burst reads as "up and out" rather
            // than sinking straight into gravity from the first frame.
            vy: Math.sin(angle) * speed - profile.speed * 0.3,
            color,
            shape: profile.shape,
            glow: profile.glow,
            maxBlurPx: profile.maxBlurPx,
            fade: profile.fade,
            gravity: profile.gravity,
            lifeMs: profile.lifeMs * (0.85 + this.rng() * 0.3),
            sizeCells: 0.16 + this.rng() * 0.12,
          });
        }
      }
    }

    const centerRow = rows.reduce((sum, row) => sum + row, 0) / rows.length + 0.5;
    const centerCol = cols / 2;

    if (backToBack) {
      for (let i = 0; i < B2B_GLINT_COUNT; i++) {
        const angle = this.rng() * Math.PI * 2;
        const speed = profile.speed * 1.2;
        this.particles.spawn({
          x: centerCol + (this.rng() - 0.5) * cols,
          y: centerRow,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          color: profile.accent,
          shape: 'spark',
          glow: true,
          maxBlurPx: Math.max(profile.maxBlurPx, 8),
          fade: 'linear',
          gravity: profile.gravity,
          lifeMs: profile.lifeMs * 1.1,
          sizeCells: 0.14,
        });
      }
      this.accentRing = {
        elapsedMs: 0,
        durationMs: B2B_RING_DURATION_MS,
        x: centerCol,
        y: centerRow,
        maxRadiusCells: cols / 2 + 1,
        color: profile.accent,
      };
    }

    if (quad) {
      this.quadRing = {
        elapsedMs: 0,
        durationMs: QUAD_RING_DURATION_MS,
        x: centerCol,
        y: centerRow,
        maxRadiusCells: cols / 2 + 2,
        color: profile.accent,
      };
    }
  }

  /**
   * Flash the cells a reconciliation just put this player's piece on.
   *
   * An empty list clears the cue rather than starting an invisible one, so a
   * caller does not have to check before calling.
   */
  triggerCorrection(cells: readonly Vec2[]): void {
    this.correctionCells = cells.length === 0 ? [] : [...cells];
    this.correctionElapsedMs = cells.length === 0 ? CORRECTION_DURATION_MS : 0;
  }

  /** Nudge the shake toward `intensityPx`, capped at `maxShakePx`. */
  triggerShake(intensityPx: number): void {
    if (this.reducedMotion()) return;
    this.shakeMagnitudePx = Math.min(this.maxShakePx, Math.max(this.shakeMagnitudePx, intensityPx));
    this.shakeRemainingMs = SHAKE_DURATION_MS;
  }

  /** Advance every running effect by `dtMs`. Call once per engine tick. */
  advance(dtMs: number): void {
    if (this.shakeRemainingMs > 0) {
      this.shakeRemainingMs = Math.max(0, this.shakeRemainingMs - dtMs);
      if (this.shakeRemainingMs === 0) this.shakeMagnitudePx = 0;
    }

    for (const [row, entry] of this.clearing) {
      const elapsedMs = entry.elapsedMs + dtMs;
      if (elapsedMs >= CLEAR_DURATION_MS) this.clearing.delete(row);
      else this.clearing.set(row, { kind: entry.kind, elapsedMs });
    }

    if (this.correctionElapsedMs < CORRECTION_DURATION_MS) {
      this.correctionElapsedMs += dtMs;
      if (this.correctionElapsedMs >= CORRECTION_DURATION_MS) this.correctionCells = [];
    }

    this.particles.advance(dtMs);

    if (this.quadRing !== null) {
      this.quadRing.elapsedMs += dtMs;
      if (this.quadRing.elapsedMs >= this.quadRing.durationMs) this.quadRing = null;
    }
    if (this.accentRing !== null) {
      this.accentRing.elapsedMs += dtMs;
      if (this.accentRing.elapsedMs >= this.accentRing.durationMs) this.accentRing = null;
    }
  }

  /** A read-only snapshot for this frame's render call. */
  snapshot(): EffectsSnapshot {
    const clearingRows = new Map<number, ClearingRow>();
    for (const [row, entry] of this.clearing) {
      clearingRows.set(row, {
        kind: entry.kind,
        progress: Math.min(1, entry.elapsedMs / CLEAR_DURATION_MS),
      });
    }

    let shakeX = 0;
    let shakeY = 0;
    if (this.shakeRemainingMs > 0) {
      const falloff = this.shakeRemainingMs / SHAKE_DURATION_MS;
      const magnitude = this.shakeMagnitudePx * falloff;
      shakeX = Math.round((this.rng() - 0.5) * 2 * magnitude);
      shakeY = Math.round((this.rng() - 0.5) * 2 * magnitude);
    }

    const correction =
      this.correctionCells.length === 0
        ? null
        : {
            cells: this.correctionCells,
            progress: Math.min(1, this.correctionElapsedMs / CORRECTION_DURATION_MS),
          };

    return {
      shakeX,
      shakeY,
      clearingRows,
      correction,
      particles: this.particles.snapshot(),
      quadRing: ringSnapshot(this.quadRing),
      accentRing: ringSnapshot(this.accentRing),
    };
  }
}
